/**
 * App-wide chip for typed RAT entities (tables, rulesets, properties, …).
 *
 * Appearance and optional link/tooltip come from LinkChipType via
 * LINK_CHIP_TYPE_CONFIG — callers pass id, name, and chipType only.
 * Usable inside DataGrid cells or anywhere else in the UI.
 */
import React, { useMemo } from "react";
import { Chip, SxProps, Theme, Tooltip } from "@mui/material";
import { useTypedSelector } from "store";
import { selectProductLineId } from "store/reducers/products/products.selectors";
import { LINK_CHIP_TYPE_CONFIG, LinkChipTypeConfig } from "./linkChip.config";
import { LinkChipInfo, LinkChipType } from "./linkChip.types";

export type LinkChipProps = {
  readonly id: string;
  readonly name: string;
  readonly chipType: LinkChipType;
};

export function LinkChip({ id, name, chipType }: LinkChipProps) {
  // Link builders (property / table) need the active product line from Redux.
  const productLineId: string = useTypedSelector(selectProductLineId) ?? "";
  const config: LinkChipTypeConfig = LINK_CHIP_TYPE_CONFIG[chipType];
  const chipInfo: LinkChipInfo = useMemo(() => ({ id, name }), [id, name]);

  const href: string = config.getHref?.(chipInfo, productLineId) ?? "";
  const tooltip: string | undefined = config.getTooltip?.(
    chipInfo,
    productLineId
  );
  const sx: SxProps<Theme> | undefined = config.getSx?.(chipInfo) ?? config.sx;
  const hasHref = Boolean(href);

  const chip: React.ReactElement = (
    <Chip
      {...(hasHref
        ? {
            component: "a" as const,
            href,
            target: "_blank" as const,
            rel: "noopener noreferrer",
            clickable: true,
            // Keep row/cell click handlers from also firing when opening a link.
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          }
        : {})}
      label={name}
      size="small"
      variant={config.variant}
      color={config.color}
      sx={sx}
    />
  );

  if (!tooltip) {
    return chip;
  }

  // Tooltip needs a DOM node that accepts a ref; wrap Chip in <span>.
  return (
    <Tooltip title={tooltip} arrow>
      <span>{chip}</span>
    </Tooltip>
  );
}

export default LinkChip;
